import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { adminAPI } from '../../services/api';
import { FileText, Users, GitMerge, Activity, Shield, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalDeals: 0,
    pendingDeals: 0,
    activeDeals: 0,
    completedDeals: 0,
    pendingVerifications: 0,
    approvedLSPs: 0,
    pendingCommitments: 0,
    approvedCommitments: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [dealsRes, verificationsRes, commitmentsRes] = await Promise.all([
        adminAPI.getAllDeals().catch(() => ({ data: [] })),
        adminAPI.getAllVerifications().catch(() => ({ data: [] })),
        adminAPI.getAllCommitments().catch(() => ({ data: [] }))
      ]);

      const deals = dealsRes.data;
      const verifications = verificationsRes.data;
      const commitments = commitmentsRes.data;

      setStats({
        totalDeals: deals.length,
        pendingDeals: deals.filter(d => d.status === 'submitted').length,
        activeDeals: deals.filter(d => d.status === 'active').length,
        completedDeals: deals.filter(d => d.status === 'completed').length,
        pendingVerifications: verifications.filter(v => v.verification_status === 'pending').length,
        approvedLSPs: verifications.filter(v => v.verification_status === 'approved').length,
        pendingCommitments: commitments.filter(c => c.commitment_status === 'requested').length,
        approvedCommitments: commitments.filter(c => c.commitment_status === 'admin_approved').length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'Manage Deals',
      description: 'Review and approve deal submissions',
      icon: FileText,
      gradient: 'gradient-primary',
      link: '/admin/deals',
      badge: stats.pendingDeals,
      badgeColor: 'bg-red-500'
    },
    {
      title: 'LSP Verifications',
      description: 'Approve liquidity verifications',
      icon: Shield,
      gradient: 'gradient-lsp',
      link: '/admin/lsp-verifications',
      badge: stats.pendingVerifications,
      badgeColor: 'bg-yellow-500'
    },
    {
      title: 'Commitments',
      description: 'Match LSPs with deals',
      icon: GitMerge,
      gradient: 'gradient-success',
      link: '/admin/commitments',
      badge: stats.pendingCommitments,
      badgeColor: 'bg-orange-500'
    },
    {
      title: 'Audit Logs',
      description: 'View system activity logs',
      icon: Activity,
      gradient: 'gradient-admin',
      link: '/admin/audit-logs',
      badge: null,
      badgeColor: ''
    },
    {
      title: 'Document Review',
      description: 'Review and approve signed documents',
      icon: FileText,
      gradient: 'gradient-accent',
      link: '/admin/document-review',
      badge: null,
      badgeColor: 'bg-amber-500'
    }
  ];

  return (
    <Layout title="Admin Dashboard">
      <div className="space-y-8 animate-slide-in-up" data-testid="admin-dashboard">
        {/* Stats Overview */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-orange-500" />
            Platform Overview
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Total Deals</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.totalDeals}</p>
                  </div>
                  <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center shadow-lg animate-pulse-glow">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in" style={{animationDelay: '0.1s'}}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Pending Review</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.pendingDeals}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                    <AlertCircle className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in" style={{animationDelay: '0.2s'}}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Active Deals</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.activeDeals}</p>
                  </div>
                  <div className="w-12 h-12 gradient-success rounded-xl flex items-center justify-center shadow-lg">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in" style={{animationDelay: '0.3s'}}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Completed</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.completedDeals}</p>
                  </div>
                  <div className="w-12 h-12 gradient-accent rounded-xl flex items-center justify-center shadow-lg">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in" style={{animationDelay: '0.4s'}}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Approved LSPs</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.approvedLSPs}</p>
                  </div>
                  <div className="w-12 h-12 gradient-lsp rounded-xl flex items-center justify-center shadow-lg">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in" style={{animationDelay: '0.5s'}}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">LSP Pending</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.pendingVerifications}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-xl flex items-center justify-center shadow-lg">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in" style={{animationDelay: '0.6s'}}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Commitments</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.approvedCommitments}</p>
                  </div>
                  <div className="w-12 h-12 gradient-admin rounded-xl flex items-center justify-center shadow-lg">
                    <GitMerge className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in" style={{animationDelay: '0.7s'}}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Pending Match</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.pendingCommitments}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
                    <GitMerge className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Quick Actions</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Card 
                  key={index} 
                  className="border-slate-200 dark:border-slate-700 hover:shadow-2xl transition-all group glass-effect hover-lift animate-scale-in"
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <CardHeader>
                    <div className="relative">
                      <div className={`w-14 h-14 ${action.gradient} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      {action.badge !== null && action.badge > 0 && (
                        <div className={`absolute -top-2 -right-2 w-7 h-7 ${action.badgeColor} rounded-full flex items-center justify-center shadow-lg animate-pulse`}>
                          <span className="text-white text-xs font-bold">{action.badge}</span>
                        </div>
                      )}
                    </div>
                    <CardTitle className="text-slate-900 dark:text-slate-100">{action.title}</CardTitle>
                    <CardDescription>{action.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to={action.link}>
                      <Button className={`w-full ${action.gradient} border-0 hover:shadow-lg transition-all hover-scale`}>
                        Manage
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Admin Notes */}
        <Card className="gradient-overlay border-orange-200 dark:border-orange-800 glass-effect animate-slide-in-up">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Activity className="w-5 h-5 text-orange-500" />
              Admin Responsibilities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Deal Management</h4>
                <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Review and approve new deal submissions
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Control deal visibility in marketplace
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Manage deal status throughout lifecycle
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">LSP & Matching</h4>
                <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                    Verify LSP liquidity and documentation
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                    Approve commitments and matches
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                    Coordinate closing process
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminDashboard;